import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode
} from "react";
import { getTailwindBreakpointValue } from "@/utils/tailwind";

interface SidebarContextType {
  isSidebarOpen: boolean;
  setSidebarOpen: (value: boolean | ((prevState: boolean) => boolean)) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({
  children
}) => {
  const STORAGE_KEY = "sidebarOpen";
  const minTailwindBreakpoint = getTailwindBreakpointValue("md");
  const isWideScreen = window.innerWidth > minTailwindBreakpoint;

  const getInitialState = () => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);

      if (savedState !== null) {
        return JSON.parse(savedState);
      }

      return isWideScreen;
    } catch {
      return isWideScreen;
    }
  };

  const [isSidebarOpen, setIsSidebarOpenInternal] = useState(getInitialState);
  // Tracks whether the user manually collapsed the sidebar — sticky until they manually expand
  const wasManuallyCollapsed = useRef(!getInitialState() && isWideScreen);

  const setSidebarOpen = (
    value: boolean | ((prevState: boolean) => boolean)
  ) => {
    const newValue =
      typeof value === "function"
        ? (value as (prevState: boolean) => boolean)(isSidebarOpen)
        : value;

    // Manual collapse is sticky; manual expand clears the flag
    wasManuallyCollapsed.current = !newValue;
    setIsSidebarOpenInternal(newValue);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newValue));
    } catch {
      // localStorage unavailable
    }
  };

  // Auto-collapse/expand sidebar on window resize
  useEffect(() => {
    const handleResize = () => {
      const isWide = window.innerWidth > minTailwindBreakpoint;

      setIsSidebarOpenInternal((prev: boolean) => {
        // Going narrow → always auto-collapse
        if (!isWide && prev) {
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(false));
          } catch {
            // localStorage unavailable
          }
          return false;
        }

        // Going wide → only auto-expand if the user didn't manually collapse
        if (isWide && !prev && !wasManuallyCollapsed.current) {
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(true));
          } catch {
            // localStorage unavailable
          }
          return true;
        }

        return prev;
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [minTailwindBreakpoint]);

  return (
    <SidebarContext.Provider value={{ isSidebarOpen, setSidebarOpen }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebarState = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error("useSidebarState must be used within a SidebarProvider");
  }
  return context;
};
